import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Observable, tap } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';

import { AdminDataService } from '../../controller/admin-data.service';
import { AdminUserResponse, UserCreateRequest } from '@solverml/api';
import { UserListComponent } from "../user-list/user-list.component";
import { PermissionEditorComponent } from "../permission-editor/permission-editor.component";
import { RfebmPrimaryButtonComponent } from "../../../../ui/atoms/rfebm-primary-button/rfebm-primary-button.component";
import { CreateUserFormComponent } from "../create-user-form/create-user-form.component";

@Component({
  selector: 'app-user-management',
  standalone: true,
  imports: [
    CommonModule,
    UserListComponent,
    PermissionEditorComponent,
    RfebmPrimaryButtonComponent,
    MatSnackBarModule // Añadido para soportar el snack-bar
  ],
  templateUrl: './user-management.component.html',
  styleUrls: ['./user-management.component.scss']
})
export class UserManagementComponent implements OnInit {

  public users$: Observable<AdminUserResponse[]>;
  public selectedUser: AdminUserResponse | null = null;
  public isDeleting = signal<boolean>(false);

  constructor(
    private adminDataService: AdminDataService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.loadUsers();
  }

  /**
   * Lógica de borrado de usuario con confirmación nativa y notificación vía SnackBar.
   */
  public onDeleteUser(): void {
    const user = this.selectedUser;
    if (!user) return;

    const confirmed = confirm(`¿Estás seguro de que deseas eliminar permanentemente a ${user.name}? Esta acción no se puede deshacer.`);

    if (!confirmed) return;

    this.isDeleting.set(true);

    this.adminDataService.deleteUser(user.idHash).subscribe({
      next: () => {
        this.snackBar.open('Usuario eliminado correctamente', 'Cerrar', { duration: 3000 });
        this.selectedUser = null; // Limpiamos la selección
        this.loadUsers(); // Refrescamos la lista
        this.isDeleting.set(false);
      },
      error: (err) => {
        this.snackBar.open('Error al eliminar el usuario', 'Cerrar', { duration: 5000 });
        this.isDeleting.set(false);
        console.error(err);
      }
    });
  }

  /**
   * Llama al servicio para obtener la lista de usuarios.
   */
  loadUsers(): void {
    this.users$ = this.adminDataService.getUsers();
  }

  /**
   * Recibe el usuario seleccionado desde el componente hijo UserListComponent.
   */
  onUserSelected(user: AdminUserResponse | null): void {
    this.selectedUser = user;
  }

  /**
   * Se activa cuando el editor de permisos ha guardado los cambios.
   */
  onPermissionsSaved(): void {
    this.selectedUser = null;
    this.loadUsers();
  }

  openCreateUserModal(): void {
    const dialogRef = this.dialog.open(CreateUserFormComponent, {
      width: '600px',
      autoFocus: 'input',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.handleUserCreation(result);
      }
    });
  }

  private handleUserCreation(userData: UserCreateRequest): void {
    this.adminDataService.createUser(userData).subscribe({
      next: (response) => {
        this.snackBar.open('Usuario creado con éxito', 'OK', { duration: 3000 });
        this.users$ = this.adminDataService.getUsers().pipe(
          tap(users => {
            const newUser = users.find(u => u.idHash === response.user_id);
            this.selectedUser = newUser || null;
          })
        );
      },
      error: (err) => {
        this.snackBar.open('Error al crear el usuario', 'Cerrar', { duration: 5000 });
        console.error('Error al crear el usuario', err);
      }
    });
  }
}
